import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  GridItem,
  Heading,
  Stack,
  Stat,
  Table,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { DashboardResourceLink } from "./DashboardResourceLink"
import { PercentileRankingCard } from "./PercentileRankingCard"
import { StudentMplTransmutationCard } from "./StudentMplTransmutationCard"

import { PerformanceRanking } from "@/components/Analytics"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { Button } from "@/components/ui/button"
import { useStudentDashboard } from "@/hooks/useDashboardQueries"

export default function StudentDashboard() {
  const { data, isLoading } = useStudentDashboard()
  const quizLink = (quizId: string) => ({
    to: "/quizzes/$quizId" as const,
    params: { quizId },
  })
  const feedbackLink = (questionId: string, feedbackId?: string) => ({
    to: "/questions/$questionId/feedback" as const,
    params: { questionId },
    search: feedbackId ? { feedbackId } : undefined,
  })

  if (isLoading) {
    return <Text>Loading dashboard...</Text>
  }

  if (!data) {
    return (
      <AppEmptyState
        title={emptyStateCopy.analytics.noData.title}
        description={emptyStateCopy.analytics.noData.description}
      />
    )
  }

  const hasMplAnalytics = data.completed_attempts.some(
    (attempt) => attempt.allow_mpl_transmutation,
  )
  const hasPercentileAnalytics = data.completed_attempts.some(
    (attempt) =>
      attempt.show_percentile && typeof attempt.percentile === "number",
  )
  const hasChartAnalytics =
    data.performance_trends.length > 0 ||
    data.subject_performance.length > 0 ||
    data.areas_to_improve.length > 0
  const hasAnyAnalytics =
    hasMplAnalytics || hasPercentileAnalytics || hasChartAnalytics
  const hasAnyReleasedScore = data.completed_attempts.some(
    (attempt) => attempt.score !== null,
  )

  return (
    <Stack gap={6} mt={1}>
      {/* Stats row — always visible above tabs */}
      <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
        <GridItem>
          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Total Quizzes Completed</Stat.Label>
                <Stat.ValueText>{data.total_quizzes_completed}</Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </GridItem>
        {data.overall_avg_score_percentage !== null && hasAnyReleasedScore && (
          <GridItem>
            <Card.Root>
              <Card.Body>
                <Stat.Root>
                  <Stat.Label>Overall Average Score</Stat.Label>
                  <Stat.ValueText>
                    {`${data.overall_avg_score_percentage.toFixed(1)}%`}
                  </Stat.ValueText>
                </Stat.Root>
              </Card.Body>
            </Card.Root>
          </GridItem>
        )}
        <GridItem>
          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Scheduled Results Pending</Stat.Label>
                <Stat.ValueText>
                  {data.scheduled_results_pending}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </GridItem>
        {data.feedback_enabled && (
          <GridItem>
            <Card.Root>
              <Card.Body>
                <Stat.Root>
                  <Stat.Label>Pending Feedback</Stat.Label>
                  <Stat.ValueText>{data.pending_feedback_count}</Stat.ValueText>
                </Stat.Root>
              </Card.Body>
            </Card.Root>
          </GridItem>
        )}
      </Grid>

      {/* Tabbed content */}
      <Tabs.Root defaultValue="overview">
        <Tabs.List overflowX="auto" overflowY="hidden" maxW="100%">
          <Tabs.Trigger value="overview" flexShrink={0}>
            Overview
          </Tabs.Trigger>
          <Tabs.Trigger value="analytics" flexShrink={0}>
            Analytics
          </Tabs.Trigger>
          {data.feedback_enabled && (
            <Tabs.Trigger value="feedback" flexShrink={0}>
              Feedback
            </Tabs.Trigger>
          )}
        </Tabs.List>

        {/* Overview tab */}
        <Tabs.Content value="overview">
          <Stack gap={6} pt={4}>
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
              {/* Recent Attempts */}
              <GridItem minW={0}>
                <Card.Root>
                  <Card.Header>
                    <Heading size="md">Recent Attempts</Heading>
                  </Card.Header>
                  <Card.Body>
                    {data.recent_attempts.length === 0 ? (
                      <AppEmptyState
                        variant="compact"
                        title={emptyStateCopy.attempts.none.title}
                        description={emptyStateCopy.attempts.none.description}
                      />
                    ) : (
                      <>
                        {/* Desktop */}
                        <Box display={{ base: "none", md: "block" }}>
                          <Table.Root size="sm">
                            <Table.Header>
                              <Table.Row>
                                <Table.ColumnHeader>Quiz</Table.ColumnHeader>
                                <Table.ColumnHeader>Score</Table.ColumnHeader>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {data.recent_attempts
                                .slice(0, 5)
                                .map((attempt) => (
                                  <Table.Row key={attempt.quiz_id}>
                                    <Table.Cell maxW="250px">
                                      <Flex align="center" gap={2}>
                                        <Text flex="1" minW={0} truncate>
                                          {attempt.quiz_name}
                                        </Text>
                                        <DashboardResourceLink
                                          label="Open quiz"
                                          {...quizLink(attempt.quiz_id)}
                                        />
                                      </Flex>
                                    </Table.Cell>
                                    <Table.Cell>
                                      {attempt.score !== null
                                        ? `${attempt.score}/${attempt.total_questions}`
                                        : "Pending"}
                                    </Table.Cell>
                                  </Table.Row>
                                ))}
                            </Table.Body>
                          </Table.Root>
                        </Box>

                        {/* Mobile cards */}
                        <VStack
                          display={{ base: "flex", md: "none" }}
                          gap={3}
                          align="stretch"
                        >
                          {data.recent_attempts.slice(0, 5).map((attempt) => (
                            <Card.Root key={attempt.quiz_id}>
                              <Card.Body>
                                <Flex justify="space-between" align="center">
                                  <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    flex="1"
                                    minW={0}
                                    truncate
                                  >
                                    {attempt.quiz_name}
                                  </Text>
                                  <DashboardResourceLink
                                    label="Open quiz"
                                    {...quizLink(attempt.quiz_id)}
                                  />
                                </Flex>
                                <Text fontSize="sm" color="fg.muted" mt={1}>
                                  Score:{" "}
                                  {attempt.score !== null
                                    ? `${attempt.score}/${attempt.total_questions}`
                                    : "Pending"}
                                </Text>
                              </Card.Body>
                            </Card.Root>
                          ))}
                        </VStack>
                      </>
                    )}
                  </Card.Body>
                </Card.Root>
              </GridItem>

              {/* Available Quizzes */}
              <GridItem minW={0}>
                <Card.Root>
                  <Card.Header>
                    <Heading size="md">Available Quizzes</Heading>
                  </Card.Header>
                  <Card.Body>
                    {data.available_quizzes.length === 0 ? (
                      <AppEmptyState
                        variant="compact"
                        title={emptyStateCopy.quizzes.available.title}
                        description={
                          emptyStateCopy.quizzes.available.description
                        }
                      />
                    ) : (
                      <VStack align="stretch" gap={2}>
                        {data.available_quizzes.slice(0, 5).map((quiz) => (
                          <Box
                            key={quiz.quiz_id}
                            p={3}
                            borderWidth="1px"
                            borderLeftWidth={quiz.completed ? undefined : "4px"}
                            borderLeftColor={
                              quiz.completed ? undefined : "orange.400"
                            }
                            borderRadius="md"
                          >
                            <Flex align="center" gap={3}>
                              <Box flex="1" minW={0}>
                                <Text fontWeight="medium" truncate>
                                  {quiz.quiz_name}
                                </Text>
                                <Box mt={1}>
                                  {quiz.completed ? (
                                    <Badge colorPalette="blue" size="sm">
                                      Attempted
                                    </Badge>
                                  ) : (
                                    <Badge
                                      colorPalette="orange"
                                      variant="outline"
                                      borderColor="orange.400"
                                      size="sm"
                                    >
                                      Not attempted
                                    </Badge>
                                  )}
                                </Box>
                              </Box>
                              <DashboardResourceLink
                                label="Open quiz"
                                {...quizLink(quiz.quiz_id)}
                              />
                            </Flex>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Card.Body>
                </Card.Root>
              </GridItem>
            </Grid>
          </Stack>
        </Tabs.Content>

        {/* Analytics tab */}
        <Tabs.Content value="analytics">
          <Stack gap={6} pt={4}>
            {!hasAnyAnalytics && (
              <Card.Root>
                <Card.Body>
                  <AppEmptyState
                    variant="compact"
                    title={emptyStateCopy.analytics.noData.title}
                    description={emptyStateCopy.analytics.noData.description}
                  />
                </Card.Body>
              </Card.Root>
            )}

            <StudentMplTransmutationCard
              completedAttempts={data.completed_attempts}
            />

            {/* Cross-quiz percentile lollipop */}
            <PercentileRankingCard
              completedAttempts={data.completed_attempts}
            />

            {/* Performance Trend */}
            {data.performance_trends.length > 0 && (
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Performance Trend</Heading>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.performance_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="avg_score_percentage"
                        stroke="#8884d8"
                        name="Avg Score %"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card.Root>
            )}

            {/* Subject Performance */}
            {data.subject_performance.length > 0 && (
              <PerformanceRanking
                title="Subject Performance"
                itemNoun="subject"
                showBands={false}
                items={data.subject_performance.map((subject) => ({
                  key: subject.subject_name,
                  name: subject.subject_name,
                  correctnessRate: subject.correctness_rate,
                }))}
              />
            )}

            {/* Areas to Improve */}
            {data.areas_to_improve.length > 0 && (
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Areas to Improve</Heading>
                </Card.Header>
                <Card.Body data-testid="areas-to-improve">
                  <Grid
                    templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                    gap={4}
                  >
                    {[...data.areas_to_improve]
                      .sort((a, b) => a.correctness_rate - b.correctness_rate)
                      .slice(0, 3)
                      .map((subject) => (
                        <Box
                          key={subject.subject_name}
                          p={4}
                          borderWidth="1px"
                          borderRadius="md"
                          bg="red.50"
                        >
                          <Text fontWeight="bold">{subject.subject_name}</Text>
                          <Text color="gray.600">
                            {subject.correctness_rate.toFixed(1)}% correct
                          </Text>
                        </Box>
                      ))}
                  </Grid>
                </Card.Body>
              </Card.Root>
            )}
          </Stack>
        </Tabs.Content>

        {/* Feedback tab */}
        {data.feedback_enabled && (
          <Tabs.Content value="feedback">
            <Stack gap={6} pt={4}>
              {data.recent_replies.length === 0 ? (
                <AppEmptyState
                  variant="compact"
                  title={emptyStateCopy.feedback.recentReplies.title}
                  description={
                    emptyStateCopy.feedback.recentReplies.description
                  }
                />
              ) : (
                <Card.Root>
                  <Card.Header>
                    <Heading size="md">Recent Teacher Replies</Heading>
                  </Card.Header>
                  <Card.Body>
                    <VStack align="stretch" gap={3}>
                      {data.recent_replies.map((reply) => (
                        <Box
                          key={reply.feedback_id}
                          p={4}
                          borderWidth="1px"
                          borderRadius="md"
                          bg="blue.50"
                        >
                          <Flex
                            direction={{ base: "column", md: "row" }}
                            align={{ base: "flex-start", md: "center" }}
                            gap={3}
                          >
                            <Box flex="1" minW={0}>
                              <Text fontSize="sm" fontWeight="medium" mb={1}>
                                {reply.quiz_name}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {reply.reply_text}
                              </Text>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {new Date(
                                  reply.replied_at,
                                ).toLocaleDateString()}
                              </Text>
                            </Box>
                            <Flex gap={1}>
                              <DashboardResourceLink
                                label="Open quiz"
                                {...quizLink(reply.quiz_id)}
                              />
                              <DashboardResourceLink
                                label="Open feedback thread"
                                {...feedbackLink(
                                  reply.question_id,
                                  reply.feedback_id,
                                )}
                              />
                            </Flex>
                          </Flex>
                        </Box>
                      ))}
                      <Button asChild variant="outline" size="sm">
                        <Link to="/feedback">View All Feedback</Link>
                      </Button>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              )}
            </Stack>
          </Tabs.Content>
        )}
      </Tabs.Root>
    </Stack>
  )
}
